import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect } from "react";
import { useStore } from "zustand";
import type { LabelsDisplayMode } from "@designboticteam/workspace-3d";
import { Section } from "@/components/Section";
import { useEngineApi } from "@/engine";

const DISPLAY_MODE_OPTIONS: Array<{ label: string; value: LabelsDisplayMode }> = [
	{ label: "Off", value: "off" },
	{ label: "All", value: "all" },
	{ label: "Selected", value: "selected" },
];

const ON_OFF_OPTIONS: Array<{ label: string; value: "on" | "off" }> = [
	{ label: "Off", value: "off" },
	{ label: "On", value: "on" },
];

const SettingsSection = (): React.ReactNode => {
	const api = useEngineApi();
	const nameLabelsDisplayMode = useStore(api.workspace3D.store, (state) => state.tools.nameLabelsDisplayMode);
	const dimensionLabelsDisplayMode = useStore(api.workspace3D.store, (state) => state.tools.dimensionLabelsDisplayMode);
	const isSnappingEnabled = useStore(api.workspace3D.store, (state) => state.cursor.isSnappingEnabled);
	const areParcelsVisible = useStore(
		api.maplibre.store,
		(state) => state.sources.pmTiles.get(api.maplibre.parcels.sourceId)?.displaySettings.isVisible ?? false,
	);

	useEffect(() => {
		api.maplibre.parcels.layer.setVisible(true);
		api.workspace3D.cursor.setSnappingEnabled(true);
	}, [api.maplibre.parcels.layer.setVisible, api.workspace3D.cursor.setSnappingEnabled]);

	return (
		<Section title="Settings" isCollapsible={true} isCollapsedByDefault={true}>
			<Stack direction="column" sx={{ gap: 2 }}>
				<Stack direction="column" sx={{ gap: 1 }}>
					<Typography variant="subtitle2">Display zone types</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						value={nameLabelsDisplayMode}
						onChange={(_, nextValue) => {
							api.workspace3D.tools.setNameLabelsDisplayMode(nextValue);
						}}
					>
						{DISPLAY_MODE_OPTIONS.map(({ label, value }) => (
							<ToggleButton key={value} value={value} size="small">
								<Typography variant="body2" sx={{ textTransform: "none" }}>
									{label}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>

				<Stack direction="column" sx={{ gap: 1 }}>
					<Typography variant="subtitle2">Display dimensions</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						value={dimensionLabelsDisplayMode}
						onChange={(_, nextValue) => {
							api.workspace3D.tools.setDimensionLabelsDisplayMode(nextValue);
						}}
					>
						{DISPLAY_MODE_OPTIONS.map(({ label, value }) => (
							<ToggleButton key={value} value={value} size="small">
								<Typography variant="body2" sx={{ textTransform: "none" }}>
									{label}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>

				<Stack direction="column" sx={{ gap: 1 }}>
					<Typography variant="subtitle2">Cursor snapping</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						value={isSnappingEnabled ? "on" : "off"}
						onChange={(_, nextValue) => {
							if (nextValue === null) {
								return;
							}
							api.workspace3D.cursor.setSnappingEnabled(nextValue === "on");
						}}
					>
						{ON_OFF_OPTIONS.map(({ label, value }) => (
							<ToggleButton key={value} value={value} size="small">
								<Typography variant="body2" sx={{ textTransform: "none" }}>
									{label}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>

				<Stack direction="column" sx={{ gap: 1 }}>
					<Typography variant="subtitle2">Display parcels</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						value={areParcelsVisible ? "on" : "off"}
						onChange={(_, nextValue) => {
							if (nextValue === null) {
								return;
							}

							api.maplibre.parcels.layer.setVisible(nextValue === "on");
						}}
					>
						{ON_OFF_OPTIONS.map(({ label, value }) => (
							<ToggleButton key={value} value={value} size="small">
								<Typography variant="body2" sx={{ textTransform: "none" }}>
									{label}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>
			</Stack>
		</Section>
	);
};

export { SettingsSection };
